import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceElement =
      event.target instanceof Element ? event.target : null;

    // Preserve the existing modal/focus-restoration behavior.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      sourceElement?.closest('dialog:modal') ?? null
    );

    /*
      Collection-card Quick Add gets special drawer timing.

      Horizon remains the owner of data-added.
      AOCTANT merely observes its END.
    */
    const sourceProductCard =
      sourceElement?.closest('product-card') ?? null;

    const sourceButton =
      sourceProductCard
        ? (
            sourceElement
              ?.closest('product-form-component')
              ?.querySelector('.quick-add__button') ??
            sourceProductCard.querySelector('.quick-add__button')
          )
        : null;

    const delayForCollectionQuickAdd =
      shouldAutoOpen &&
      sourceButton instanceof HTMLElement;

    const waitForNativeSuccessEnd = () => {
      if (
        !delayForCollectionQuickAdd ||
        !(sourceButton instanceof HTMLElement)
      ) {
        return Promise.resolve();
      }

      /*
        If Horizon has already completed data-added by the time
        the real cart promise settles, there is nothing left to wait for.
      */
      if (!sourceButton.hasAttribute('data-added')) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const observer = new MutationObserver(() => {
          if (sourceButton.hasAttribute('data-added')) return;

          observer.disconnect();

          /*
            MutationObserver runs before the next browser paint.
            Opening here catches the exact native success endpoint,
            before the returned ADD TO CART state gets its next paint.
          */
          resolve();
        });

        observer.observe(sourceButton, {
          attributes: true,
          attributeFilter: ['data-added'],
        });
      });
    };

    /*
      Existing immediate-open behavior remains for every other
      Add-to-Cart source.

      Collection-card Quick Add explicitly skips this path.
    */
    if (
      shouldAutoOpen &&
      !sourceModal &&
      !delayForCollectionQuickAdd &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(async ({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        /*
          Preserve native Quick Add modal focus restoration.
        */
        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );

          return;
        }

        /*
          Collection card:
          cart must be successful AND native success must reach
          the exact data-added endpoint.
        */
        if (delayForCollectionQuickAdd) {
          await waitForNativeSuccessEnd();
        }

        openAndSettle();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };
  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
